import { logger } from "../utils/logger";
import { apiClient } from "./axiosInstance";
import { tripRegistryClient } from "./tripRegistryClient";

// Auth0 errors that silent renewal can't resolve - the user has to go through
// the hosted login page again.
const REQUIRES_LOGIN = new Set([
  "login_required",
  "consent_required",
  "mfa_required",
]);

let loginRedirectInProgress = false;

/**
 * Send the user back through Auth0 login when their session can't be renewed,
 * remembering the current URL so they return to it afterwards.
 */
function redirectToLoginIfNeeded(error, loginWithRedirect) {
  if (!loginWithRedirect || loginRedirectInProgress) return;
  if (!REQUIRES_LOGIN.has(error?.error)) return;
  // Share pages are public and viewed without a session.
  if (window.location.pathname.startsWith("/share")) return;
  // The Auth0 callback is still being processed; it will navigate on its own.
  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) return;

  loginRedirectInProgress = true;
  loginWithRedirect({
    appState: {
      returnTo: `${window.location.pathname}${window.location.search}`,
    },
  });
}

/**
 * Attach auth interceptors (request + response) to a given axios instance.
 * @param {import('axios').AxiosInstance} client
 * @param {Function} getAccessTokenSilently
 * @param {Function} loginWithRedirect
 * @param {object} ids - object to store/eject interceptor IDs
 */
function attachInterceptors(
  client,
  getAccessTokenSilently,
  loginWithRedirect,
  ids
) {
  if (ids.requestId !== undefined) {
    client.interceptors.request.eject(ids.requestId);
  }
  if (ids.responseId !== undefined) {
    client.interceptors.response.eject(ids.responseId);
  }

  ids.requestId = client.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          logger.warn("No access token available, blocking request");
          return Promise.reject(
            new Error("Authentication required - no token available")
          );
        }
      } catch (error) {
        logger.error("Failed to get access token:", error);
        redirectToLoginIfNeeded(error, loginWithRedirect);
        return Promise.reject(error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  ids.responseId = client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const token = await getAccessTokenSilently({ cacheMode: "off" });
          if (!token) throw new Error("No token received from Auth0");
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        } catch (refreshError) {
          logger.error("Token refresh failed:", refreshError);
          redirectToLoginIfNeeded(refreshError, loginWithRedirect);
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Setup Axios interceptors to handle authentication
 * - Request interceptor: Proactively adds fresh token to every request
 * - Response interceptor: Handles 401 errors by forcing token refresh
 * @param {Function} getAccessTokenSilently - Auth0 function to get a fresh token
 * @param {Function} loginWithRedirect - Auth0 function to restart login when renewal fails
 */
export function setupAxiosAuthInterceptor(
  getAccessTokenSilently,
  loginWithRedirect
) {
  attachInterceptors(
    apiClient,
    getAccessTokenSilently,
    loginWithRedirect,
    setupAxiosAuthInterceptor
  );
  attachInterceptors(
    tripRegistryClient,
    getAccessTokenSilently,
    loginWithRedirect,
    setupAxiosAuthInterceptor.tripRegistry ||
      (setupAxiosAuthInterceptor.tripRegistry = {})
  );
}
